"use client";

import React, { useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import { SearchFilters as Filters } from "@/types/dto/search-filters/search-filters";
import { SearchRange } from "@/types/dto/search-filters/range-filter";
import { SearchParameters } from "@/types/dto/search-filters/search-parameters";
import useFilterProcessing from "@/hooks/tour-search/use-filter-processing";
import useFilterActions from "@/hooks/tour-search/use-filter-actions";
import CountFilter from "@/components/search-filters/count-filter";
import MonthFilter from "@/components/search-filters/month-filter";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import RangeFilter from "./price-range-filter";
import WhereFilter from "./location-filter";
import MultiFilterGroup from "./mutli-filter";

interface FilterLayoutProps {
  filters: Filters[];
  ranges: SearchRange[];
  currentSearchParams?: Partial<SearchParameters>;
  onFiltersChange?: (filters: Partial<SearchParameters>) => void;
  onClearAll?: () => void;
  isLoading?: boolean;
  onClose?: () => void;
  onFilterInteraction?: (filterType: string, filterValue: string | string[], action: "add" | "remove" | "clear") => void;
}

const SearchFilters = ({
  filters = [],
  ranges = [],
  currentSearchParams = {},
  onFiltersChange,
  onClearAll,
  isLoading = false,
  onClose,
  onFilterInteraction
}: FilterLayoutProps) => {
  const t = useTranslations("SEARCH");
  const formRef = useRef<HTMLFormElement>(null);
  const [shouldClearLocation, setShouldClearLocation] = useState(false);
  // Extract filter processing logic to custom hook
  const {
    locationFilter,
    badgesFilter,
    specialOffersFilter,
    smallGroupFilter,
    travelStylesCustomLabel,
    durationCustomLabel,
    activityLevelFilter,
    customerReviewFilter,
    rangesFilter,
    months
  } = useFilterProcessing({ filters, ranges });
  // Store the filter interaction and mark that we're waiting for results
  
  // Extract filter actions logic to custom hook
  const {
    filterValues,
    priceRange,
    handleFilterChange,
    handleClearField,
    handlePriceRangeChange,
    handleClearAll
  } = useFilterActions({
    currentSearchParams,
    onFiltersChange,
    onClearAll,
    onFilterInteraction
  });
  // Pure UI rendering with no business logic
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };
  // Function to explicitly clear just the location filter
  const clearLocationFilter = () => {
    setShouldClearLocation(true);
  };
  const handleLocationClearComplete = () => {
    setShouldClearLocation(false); // Reset the flag
  };
  const handleCustomClearAll = () => {
    setShouldClearLocation(true); // Tell location filter to clear
    handleClearAll(); // Clear URL parameters
  };
  return (
    <Box component="form" ref={formRef} onSubmit={handleSubmit} data-testid="search-filters-form"
      sx={{
        padding: {
          xs: "2.4rem",
          legacyDesktop: "unset"
        },
        backgroundColor: "background.default",
        width: {
          xs: "100%",
          legacyTablet: "100%",
          legacyDesktop: 286
        }
      }}>
      <Box sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between"
      }} data-testid="filters-header">
        <Typography sx={{
          mb: "1.6rem",
          color: "text.primary",
          fontSize: "24px",
          fontWeight: 500
        }} data-testid="filters-title">
          {t("Filters")}
        </Typography>
        {/* Clear All Button */}
        <ColletteButton
          variant="outlined"
          size="large"
          onClick={handleCustomClearAll}
          label={t("Clear All")}
          testId="clear-all-button"
          sx={{
            display: { xs: "none", legacyDesktop: "flex" }
          }}
        />
        <IconButton
          edge="start"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{ display: { xs: "flex", legacyDesktop: "none" } }}
          data-testid="filters-close-button"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Typography
        sx={{
          mt: "1.2rem",
          mb: "1.6rem",
          fontSize: "1.2rem",
          letterSpacing: "0.1rem",
          "&::before": { content: "\"*\"", ariaHidden: "true" }
        }}
        data-testid="filters-currency-disclaimer"
      >
        {t("All prices are in USD")}
      </Typography>
      <Stack sx={{ px: { xs: "0", sm: "12rem", legacyDesktop: "0" } }}>
        {(badgesFilter || specialOffersFilter || smallGroupFilter) && (
          <MultiFilterGroup
            title={t("Popular")}
            filters={[badgesFilter, specialOffersFilter, smallGroupFilter].filter(Boolean)}
            onChange={handleFilterChange}
            values={filterValues}
            isLoading={isLoading}
          />
        )}
        {locationFilter && locationFilter.length > 0 && (
          <WhereFilter
            filterData={locationFilter}
            onChange={handleFilterChange}
            shouldClear={shouldClearLocation}
            onClearComplete={handleLocationClearComplete}
            onSelectionMade={() => {
              if (!isLoading) {
                setTimeout(() => clearLocationFilter(), 2000);
              }
            }}
            currentValues={[
              ...(filterValues.Continent || []),
              ...(filterValues.Country || []),
              ...(filterValues.Region || []),
              ...(filterValues.City || [])
            ]} // Pass all current location values
            isLoading={isLoading} />
        )}

        {months.length > 0 && (
          <MonthFilter
            months={months}
            selectedMonths={filterValues.months}
            onChange={handleFilterChange}
            clearMonths={handleClearField}
          />
        )}
        {rangesFilter && (
          <RangeFilter
            filterData={rangesFilter}
            onChange={handlePriceRangeChange}
            currentUserPriceRange={priceRange}
            isLoading={isLoading} />
        )}

        {durationCustomLabel && (
          <CountFilter
            filterData={durationCustomLabel}
            onChange={handleFilterChange}
            value={filterValues[durationCustomLabel.fieldName] || filterValues.TourLengthDescription}
            isLoading={isLoading} />
        )}

        {travelStylesCustomLabel && (
          <CountFilter
            filterData={travelStylesCustomLabel}
            onChange={handleFilterChange}
            value={filterValues[travelStylesCustomLabel.fieldName] || filterValues.TourStyle}
            isLoading={isLoading} />
        )}

        {activityLevelFilter && (
          <CountFilter
            filterData={activityLevelFilter}
            onChange={handleFilterChange}
            value={filterValues.ActivityLevel}
            isLoading={isLoading} />
        )}

        {customerReviewFilter && (
          <CountFilter
            filterData={customerReviewFilter}
            onChange={handleFilterChange}
            value={filterValues.CustomerReview}
            isLoading={isLoading} />
        )}
      </Stack>
    </Box>
  );
};

export default SearchFilters;